import { useMemo, useState } from "react";
import { Card, DataTable, Badge, Button, Text, Link, Tooltip, EmptyState, BlockStack, InlineStack } from "@shopify/polaris";
import { buildWorkbookXml, downloadWorkbook, excelCell, excelRow } from "../../../shared/components/excelExport";

// PO chase list: open purchase orders that are overdue or arriving after
// production needs them, prioritized by the MRP run (engine section 11b).
//   CRITICAL — past the promised date and demand in the horizon is waiting
//   HIGH     — will arrive after its need date, or no usable date while demand waits
//   MEDIUM   — overdue but nothing in the horizon needs it (chase or push out)

const severityTone = { CRITICAL: "critical", HIGH: "warning", MEDIUM: "attention" };

const buildSheetRows = (poFollowUp) => {
  const rows = [
    excelRow([excelCell("POs to chase — prioritized", "Title")]),
    excelRow([excelCell(`Generated ${new Date().toISOString().substring(0, 10)}`)]),
    "<Row />",
    excelRow([
      excelCell("Priority", "Header"),
      excelCell("PO", "Header"),
      excelCell("Supplier", "Header"),
      excelCell("Part ID", "Header"),
      excelCell("Description", "Header"),
      excelCell("Qty open", "Header"),
      excelCell("Promised", "Header"),
      excelCell("Days late", "Header"),
      excelCell("First needed", "Header"),
      excelCell("Blocks", "Header"),
    ]),
  ];
  poFollowUp.forEach((po) => {
    po.lines.forEach((line) => {
      rows.push(
        excelRow([
          excelCell(line.severity, line.severity === "CRITICAL" ? "Late" : undefined),
          excelCell(po.poNumber || ""),
          excelCell(po.supplierName || ""),
          excelCell(line.productId, undefined, true),
          excelCell(line.productName || ""),
          excelCell(line.qty, undefined, true),
          excelCell(line.receiptDate || "no date", line.daysLate > 0 ? "Late" : undefined),
          excelCell(line.daysLate || "", undefined, true),
          excelCell(line.needDate || ""),
          excelCell((line.consumedBy || []).join(" · ")),
        ])
      );
    });
  });
  return rows.join("");
};

const exportChaseList = (poFollowUp) => {
  const xml = buildWorkbookXml([{ name: "POs to chase", rowsXml: buildSheetRows(poFollowUp) }]);
  downloadWorkbook(xml, `pos-to-chase-${new Date().toISOString().substring(0, 10)}.xls`);
};

export function PoFollowUp({ poFollowUp }) {
  const [severityFilter, setSeverityFilter] = useState("ALL");

  const filtered = useMemo(
    () => poFollowUp.filter((po) => severityFilter === "ALL" || po.severity === severityFilter),
    [poFollowUp, severityFilter]
  );

  if (!poFollowUp.length) {
    return (
      <Card>
        <EmptyState heading="All open POs are on track" image="">
          <p>No open purchase order is overdue or arriving after production needs it.</p>
        </EmptyState>
      </Card>
    );
  }

  const counts = { CRITICAL: 0, HIGH: 0, MEDIUM: 0 };
  poFollowUp.forEach((po) => {
    counts[po.severity] = (counts[po.severity] || 0) + 1;
  });

  const rows = filtered.flatMap((po) =>
    po.lines.map((line, index) => [
      <Badge tone={severityTone[line.severity]}>{line.severity}</Badge>,
      index === 0 ? (
        <InlineStack gap="100" blockAlign="center">
          <Link removeUnderline url={`/purchases/${po.bestellingId}`}>
            {po.poNumber}
          </Link>
          {!po.sent && <Badge tone="critical">Not sent</Badge>}
        </InlineStack>
      ) : (
        ""
      ),
      index === 0 ? (
        po.supplierId ? (
          <Link removeUnderline url={`/companies/${po.supplierId}`}>
            {po.supplierName || `#${po.supplierId}`}
          </Link>
        ) : (
          po.supplierName || ""
        )
      ) : (
        ""
      ),
      <Link removeUnderline url={`/products/${line.productId}`}>
        {String(line.productName || line.productId).substring(0, 40)}
      </Link>,
      line.qty,
      line.receiptDate ? (
        <InlineStack gap="100" blockAlign="center">
          <Tooltip content={line.receiptDateSource ? `${line.receiptDateSource.toLowerCase()} date` : ""}>
            <Text as="span">{line.receiptDate}</Text>
          </Tooltip>
          {line.daysLate > 0 && <Badge tone="critical">{`${line.daysLate}d late`}</Badge>}
        </InlineStack>
      ) : (
        <Badge tone="warning">No date</Badge>
      ),
      line.needDate ? (
        <InlineStack gap="100" blockAlign="center">
          {line.needDate}
          {line.daysBlocking > 0 && (
            <Tooltip content={`Arrives ${line.daysBlocking} day(s) after it is needed`}>
              <Badge size="small" tone="warning">{`+${line.daysBlocking}d`}</Badge>
            </Tooltip>
          )}
        </InlineStack>
      ) : (
        <Text as="span" tone="subdued">
          not in horizon
        </Text>
      ),
      (line.consumedBy || []).length ? (
        <Tooltip content={(line.consumedBy || []).join(" · ")}>
          <Badge size="small" tone="info">{`${line.consumedBy.length} order${line.consumedBy.length === 1 ? "" : "s"}`}</Badge>
        </Tooltip>
      ) : (
        ""
      ),
    ])
  );

  return (
    <BlockStack gap="400">
      <InlineStack align="space-between" blockAlign="center">
        <InlineStack gap="200">
          <Button pressed={severityFilter === "ALL"} onClick={() => setSeverityFilter("ALL")}>
            All
          </Button>
          <Button pressed={severityFilter === "CRITICAL"} onClick={() => setSeverityFilter("CRITICAL")}>
            {`Critical (${counts.CRITICAL})`}
          </Button>
          <Button pressed={severityFilter === "HIGH"} onClick={() => setSeverityFilter("HIGH")}>
            {`High (${counts.HIGH})`}
          </Button>
          <Button pressed={severityFilter === "MEDIUM"} onClick={() => setSeverityFilter("MEDIUM")}>
            {`Medium (${counts.MEDIUM})`}
          </Button>
        </InlineStack>
        <Button onClick={() => exportChaseList(filtered)}>Export Excel</Button>
      </InlineStack>

      <Card>
        <BlockStack gap="200">
          <Text as="p" tone="subdued" variant="bodySm">
            Call suppliers top-to-bottom: CRITICAL is past its promised date with production waiting, HIGH arrives after the need date or has no date at all, MEDIUM is overdue without demand in the horizon.
          </Text>
          <DataTable
            verticalAlign="middle"
            increasedTableDensity
            columnContentTypes={["text", "text", "text", "text", "numeric", "text", "text", "text"]}
            headings={[<b>Priority</b>, <b>PO</b>, <b>Supplier</b>, <b>Part</b>, <b>Qty open</b>, <b>Promised</b>, <b>First needed</b>, <b>Blocks</b>]}
            rows={rows}
          />
        </BlockStack>
      </Card>
    </BlockStack>
  );
}
